import { computedValue, inventoryField, itemsCol, layerListEntryUuid, primaryPictureOf, sizeIdOf } from '../../shared/array-types';
import { fromBaseRecord } from '../compile/from-base-context';
import { phpJsonTruthy } from '../compile/php-json-values';
import { SIZE } from '../port/k1-compile/catalog-constants';
import { summonArrival } from '../spawn/summon-arrival';
import { decodeHtmlEntitiesForPlainText } from '../../shared/decode-html-entities';
import { compileDefinitionInputs } from '../authoring/location-type-ambience';
import { attachInventoryCatalog, inventoryCatalogScope, type InventorySharedCatalog } from '../compile/inventory-shared-catalog';
import type { BaseEnvelope } from '../compile/base-patch';
import { fetchSavedResponse, readSavedResponse } from '../../shared/read-saved-response';
import type { SpellVfxPreviewSummon, SpellVfxPreviewSummonOption } from '../../shared/spell-vfx-preview-payload';

type Row = Record<string, unknown>;
type Definition = (id: string) => Row | null;

/** Compile referenced presentation from the readable parent's raw closure, including private/archived children.
 * Warm hosts recover their existing catalog scope, never re-authorize each referenced child as a new root. */
export async function compilePresentationDefinitions(host: Row, ids: readonly string[],
    loadCatalog = async (url: string): Promise<unknown> => {
        const response = await fetchSavedResponse(url);
        if (!response.ok) throw new Error(`Presentation catalog unavailable: ${response.status}`);
        return readSavedResponse(response);
    }): Promise<Map<string, Row | null>> {
    let envelope = (host._entity_base ?? host._inventory_base) as BaseEnvelope | undefined;
    if (!envelope || !fromBaseRecord(envelope.base)) throw new Error('Presentation parent inputs unavailable');
    if (!envelope.metadata || !fromBaseRecord(envelope.base.catalog)) {
        const version = envelope.catalog_version, ref = envelope.base.catalog_ref;
        if (typeof version !== 'string' || typeof ref !== 'string') throw new Error('Presentation catalog reference unavailable');
        const route = version.startsWith('entity-') ? 'base_entity_catalog_inc' : 'base_inventory_catalog_inc';
        const value = await loadCatalog(`/options/api/${route}.php?version=${encodeURIComponent(version)}&globals=1&refs=${encodeURIComponent(ref)}`);
        if (!fromBaseRecord(value) || !fromBaseRecord(value.scopes) || !fromBaseRecord(value.scopes[ref]) || !fromBaseRecord(value.definitions))
            throw new Error('Presentation catalog inputs unavailable');
        const selected = inventoryCatalogScope(value as unknown as InventorySharedCatalog, { ...value.scopes[ref], catalog: value.definitions });
        envelope = attachInventoryCatalog(envelope, selected);
    }
    return compileDefinitionInputs({ shape_v: 2, definitions: envelope.base.catalog, inputs: envelope.base, engine: envelope.engine,
        catalog: { version: envelope.catalog_version ?? envelope.engine, metadata: envelope.metadata, base: {} } }, ids);
}

export function summonPreviewIds(item: Row): string[] {
    return [...new Set(['summon', 'user_choice_1_summon', 'user_choice_2_summon', 'user_choice_3_summon', 'user_choice_4_summon']
        .flatMap(key => window.ftLayerEntries(item, key).map(entry => layerListEntryUuid(entry).toUpperCase())).filter(Boolean))];
}

/** Creature shells deliberately omit media/physics. Join the page owner's definition cache before presenting them. */
export async function loadSummonPreview(item: Row, load: (id: string) => Promise<Row | null>): Promise<SpellVfxPreviewSummon | null> {
    const ids = summonPreviewIds(item);
    const children = new Map(await Promise.all(ids.map(async id => {
        const child = await load(id);
        if (!child || String(itemsCol(child, 'item_id') ?? '').toUpperCase() !== id)
            throw new Error(`Summon preview definition unavailable: ${id}`);
        return [id, child] as const;
    })));
    return summonPreview(item, id => children.get(id) ?? null);
}

/** Read the held browser catalog, including archived referenced definitions. */
export function presentationDefinition(root: Row, id: string): Row | null {
    const catalog = fromBaseRecord(root.catalog) ? root.catalog : {};
    const value = catalog[id] ?? catalog[id.toUpperCase()] ?? catalog[id.toLowerCase()];
    return fromBaseRecord(value) ? value : null;
}

/** The existing arrival planner owns portal/rise choices; this adapter adds preview art and bounds. */
export function summonPreview(item: Row, definition: Definition): SpellVfxPreviewSummon | null {
    const ids = summonPreviewIds(item);
    const options: SpellVfxPreviewSummonOption[] = [];
    for (const id of ids) {
        const child = definition(id);
        if (!child || String(itemsCol(child, 'item_id') ?? '').toUpperCase() !== id || Number(itemsCol(child, 'item_deleted') ?? 0) !== 0) continue;
        const arrival = summonArrival(child, String(itemsCol(item, 'item_id') ?? ''), {
            entries: window.ftLayerEntries, value: window.ftLayerValue, item: () => item,
        });
        const pictureRow = primaryPictureOf(child);
        const picture = itemsCol<unknown>(pictureRow, 'picture_url') ?? itemsCol<unknown>(pictureRow, 'pic_url');
        const assets = fromBaseRecord(child.assets) ? child.assets : {};
        const mesh = (Array.isArray(assets['3d']) ? assets['3d'] : []).find((row: unknown) => fromBaseRecord(row)
            && !phpJsonTruthy(row.private) && !phpJsonTruthy(row.inheritedFrom)
            && /^https:\/\/cdn\.fatetide\.com\/.+\.glb$/i.test(String(row.url ?? '')) && /^[a-f0-9]{64}$/i.test(String(row.sha256 ?? '')));
        const size = computedValue<Row>(child, 'creature', 'size');
        const tier = SIZE.find(row => row.item_id === sizeIdOf(child)?.toUpperCase());
        const base = Number(itemsCol(size, 'base') ?? tier?.base_size ?? 0) || 5;
        const height = Number(itemsCol(size, 'height') ?? 0) || base;
        const bound = (value: number): number => Math.max(1, Math.min(60, Number.isFinite(value) ? value : 5));
        options.push({ portal: arrival.portal, rise: arrival.recipe, creature: {
            id, name: String(decodeHtmlEntitiesForPlainText(String(itemsCol(child, 'item_name') ?? '')) ?? ''), picture: typeof picture === 'string' && picture !== '' ? picture : null,
            mesh: mesh ? { url: String(mesh.url), sha256: String(mesh.sha256).toLowerCase() } : null,
            heightFt: bound(height), baseFt: bound(base),
        } });
    }
    return options[0] ? { ...options[0], options } : null;
}

export interface GalleryLight { itemId: string; radiusFt: number; magical: boolean; sourceItemId: string; sourceName: string }
/** Select illumination from compiled held definitions, including their full_ability sources. */
export function lightGalleryParameters(inventory: readonly Row[], definition: Definition): Record<string, GalleryLight> {
    const result: Record<string, GalleryLight> = {};
    for (const row of inventory) {
        const itemId = String(inventoryField(row, 'inventory_item_id') ?? '').toUpperCase();
        const item = definition(itemId);
        if (!item || itemsCol(item, 'item_type') !== 'light_source') continue;
        let source = item, radius = Math.abs(Number(window.ftLayerValue(item, 'bright_light_dim_light') ?? 0));
        if (!(radius > 0)) for (const entry of window.ftLayerEntries(item, 'full_ability')) {
            const id = layerListEntryUuid(entry).toUpperCase();
            const ability = definition(id) ?? (fromBaseRecord(entry) ? entry : null);
            if (!ability) continue;
            const value = Math.abs(Number(window.ftLayerValue(ability, 'bright_light_dim_light') ?? 0));
            if (value > radius) { radius = value; source = ability; }
        }
        if (!Number.isFinite(radius) || radius <= 0) continue;
        result[String(inventoryField(row, 'inventory_id') ?? '').toUpperCase()] = { itemId, radiusFt: radius,
            magical: phpJsonTruthy(window.ftLayerValue(source, 'magical_light') ?? window.ftLayerValue(item, 'magical_light')),
            sourceItemId: String(itemsCol(source, 'item_id') ?? ''), sourceName: String(itemsCol(source, 'item_name') ?? '') };
    }
    return result;
}
